import React, { useCallback } from "react";
import { merge } from "glamor";
import { connect } from "react-redux";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import { IStore } from "reducers";
import type { IScope } from "reducers/scope";
import { fetchProducts, fetchProductsNextPage } from "actions/products";
import { IItemRecord } from "records/item/item";
import { QuickAddPopup } from "./quick-add-popup";
import { ICart } from "constants/cart";
import { format } from "lib/globalize-util";
import theme, { themeOptions } from "styles/theme";
import { DATATEST } from "constants/integration";

const useStyles = makeStyles(() =>
    createStyles({
        buttonRoot: {
            maxWidth: 0,
            minWidth: 40,
        },
        quickAddBtn: {
            borderRadius: 15,
        },
        tooltip: {
            fontSize: 12,
            fontFamily: themeOptions.palette.fontFamily,
        },
    }),
);

interface IDispatch {
    onFetchProducts: (cacheKey: string, filters: { [key: string]: string | number }) => void;
    onFetchProductsNextPage: (nextPage: string) => void;
}

interface IState {
    productsMap: { [id: string]: IItemRecord };
    productCollection: {
        results: string[];
        isFetching: boolean;
        hasMoreResults: boolean;
        nextPage: string;
    };
}

interface IProps {
    scope: IScope;
    cart: ICart;
    enabled?: boolean;
    isWorkingOrder?: boolean;
    onCartAdd: () => Promise<any>;
}

const COLLECTION_NAME = "PRODUCTS_SEARCH";

const QuickAddComponent: React.FC<IProps & IState & IDispatch> = ({
    scope,
    cart,
    enabled = true,
    isWorkingOrder = false,
    productsMap,
    productCollection,
    onFetchProducts,
    onFetchProductsNextPage,
    onCartAdd,
}) => {
    const classes = useStyles();
    const styles = {
        quickAddIcon: {
            fill: themeOptions.palette.grey,
            width: 22,
            height: 22,
            marginLeft: 13,
        },
        component: merge({
            transition: "height 0.3s ease",
            position: "relative",
            paddingTop: 15,
            height: `${theme.appBar.height}px`,
        }),
    };

    const quickAddContainer = React.useRef(null);
    const [isOpen, setIsOpen] = React.useState(false);

    const handleToggleMenu = () => setIsOpen(!isOpen);

    const isDescendant = React.useCallback((parent, child) => {
        let node = child.parentNode;

        while (node !== null) {
            if (node === parent) {
                return true;
            }
            node = node.parentNode;
        }

        return false;
    }, []);

    const handleClickAway = React.useCallback(
        (e) => {
            if (e.defaultPrevented || !isOpen) {
                return;
            }

            const el = quickAddContainer.current;

            if (
                (e.target !== el && e.target === window) ||
                (document.documentElement.contains(e.target) && !isDescendant(el, e.target))
            ) {
                setIsOpen(false);
            }
        },
        [isOpen, isDescendant],
    );

    React.useEffect(() => {
        window.addEventListener("touchstart", handleClickAway, true);
        window.addEventListener("click", handleClickAway, true);
        return () => {
            window.removeEventListener("touchstart", handleClickAway);
            window.removeEventListener("click", handleClickAway);
        };
    }, [handleClickAway]);

    // span is needed to trigger the tooltip when button is disabled
    const QuickAddBtn = React.useMemo(
        () => (
            <span data-test={DATATEST.SITE_HEADER.QUICK_ADD_BTN}>
                <Button
                    classes={{ root: classes.quickAddBtn }}
                    onClick={handleToggleMenu}
                    variant="outlined"
                    color="default"
                    disableElevation
                    disabled={!enabled}
                >
                    {format("Quick Add")}
                </Button>
            </span>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handleToggleMenu, enabled],
    );

    const handleFetchProducts = useCallback(
        // Todo: Cache key should be implemented to prevent re-fetch on each popup open
        (filters: { [key: string]: string | number }) => onFetchProducts(null, filters),
        [onFetchProducts],
    );

    return (
        <div ref={quickAddContainer} {...styles.component}>
            {isWorkingOrder && !enabled && (
                <Tooltip
                    title={format("Please go back to make changes to your order")}
                    classes={{ tooltip: classes.tooltip }}
                >
                    {QuickAddBtn}
                </Tooltip>
            )}
            {isWorkingOrder && enabled && QuickAddBtn}
            {!isWorkingOrder && (
                <Button
                    onClick={handleToggleMenu}
                    classes={{ root: classes.buttonRoot }}
                    startIcon={<ExpandMoreIcon style={styles.quickAddIcon} />}
                />
            )}
            {isOpen && enabled && (
                <QuickAddPopup
                    scope={scope}
                    cart={cart}
                    productsMap={productsMap}
                    productCollection={productCollection}
                    onFetchProducts={handleFetchProducts}
                    onFetchProductsNextPage={onFetchProductsNextPage}
                    isOpen={isOpen}
                    onClosePopup={handleToggleMenu}
                    onCartAdd={onCartAdd}
                    isWorkingOrder={isWorkingOrder}
                />
            )}
        </div>
    );
};

const mapStateToProps = (store: IStore) => ({
    productCollection: store.products.collections[COLLECTION_NAME] || {
        results: [],
        isFetching: false,
        hasMoreResults: true,
    },
    productsMap: store.entities.itemsPreview,
});
const mapDispatchToProps = (dispatch): IDispatch => ({
    onFetchProducts: (cacheKey, filters) =>
        dispatch(fetchProducts(COLLECTION_NAME, cacheKey, filters, { preview: true })),
    onFetchProductsNextPage: (nextPage) =>
        dispatch(fetchProductsNextPage(COLLECTION_NAME, nextPage, { preview: true })),
});

export const QuickAdd = connect<IState, IDispatch, IProps>(mapStateToProps, mapDispatchToProps)(QuickAddComponent);
